import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { legalAPI } from '../lib/api';
import {
  FolderOpen, Upload, Download, Plus, Search, Shield, Mail, Edit, Trash2, Loader2,
  FileText, X, Paperclip,
} from 'lucide-react';

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return ''; }
};
const fmtSize = (b) => {
  if (!b && b !== 0) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

export function LegalPage() {
  const [categories, setCategories] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCat, setActiveCat] = useState('all');

  // Upload / edit dialog
  const [docDialog, setDocDialog] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [form, setForm] = useState({ nombre: '', categoria: '', nota: '', file: null });
  const [saving, setSaving] = useState(false);

  // Category dialog
  const [catDialog, setCatDialog] = useState(false);
  const [newCat, setNewCat] = useState('');
  const [catSaving, setCatSaving] = useState(false);

  // Email dialog
  const [emailDialog, setEmailDialog] = useState(false);
  const [emailDoc, setEmailDoc] = useState(null);
  const [emailForm, setEmailForm] = useState({ to_email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await legalAPI.getData();
      setCategories(res.categories || []);
      setDocuments(res.documents || []);
    } catch (e) {
      toast.error(e.message || 'Error al cargar los documentos');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return documents.filter((d) => {
      if (activeCat !== 'all' && d.categoria !== activeCat) return false;
      if (!q) return true;
      return [d.nombre, d.categoria, d.nota, d.original_filename].filter(Boolean).some((t) => t.toLowerCase().includes(q));
    });
  }, [documents, search, activeCat]);

  const countByCat = useMemo(() => {
    const m = {};
    documents.forEach((d) => { m[d.categoria] = (m[d.categoria] || 0) + 1; });
    return m;
  }, [documents]);

  const openUpload = () => {
    setEditingDoc(null);
    setForm({ nombre: '', categoria: categories[0] || '', nota: '', file: null });
    setDocDialog(true);
  };
  const openEdit = (doc) => {
    setEditingDoc(doc);
    setForm({ nombre: doc.nombre, categoria: doc.categoria, nota: doc.nota || '', file: null });
    setDocDialog(true);
  };

  const handleSaveDoc = async () => {
    if (!editingDoc && !form.file) { toast.error('Selecciona un archivo'); return; }
    if (!form.categoria) { toast.error('Selecciona una categoría'); return; }
    setSaving(true);
    try {
      if (editingDoc) {
        await legalAPI.update(editingDoc.id, { nombre: form.nombre, categoria: form.categoria, nota: form.nota });
        toast.success('Documento actualizado');
      } else {
        await legalAPI.upload({ file: form.file, nombre: form.nombre || form.file.name, categoria: form.categoria, nota: form.nota });
        toast.success('Documento subido');
      }
      setDocDialog(false);
      await load();
    } catch (e) {
      toast.error(e.message || 'Error al guardar');
    }
    setSaving(false);
  };

  const handleDownload = (doc) => {
    const url = legalAPI.downloadUrl(doc.id);
    window.open(url, '_blank');
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`¿Eliminar "${doc.nombre}"? Esta acción no se puede deshacer.`)) return;
    setBusyId(doc.id);
    try {
      await legalAPI.delete(doc.id);
      toast.success('Documento eliminado');
      await load();
    } catch (e) {
      toast.error(e.message || 'Error al eliminar');
    }
    setBusyId(null);
  };

  const openEmail = (doc) => {
    setEmailDoc(doc);
    setEmailForm({ to_email: '', subject: `Documento: ${doc.nombre}`, message: '' });
    setEmailDialog(true);
  };
  const handleSendEmail = async () => {
    if (!emailForm.to_email.trim()) { toast.error('Introduce un email de destino'); return; }
    setSending(true);
    try {
      const res = await legalAPI.sendEmail(emailDoc.id, emailForm);
      toast.success(res.message || 'Documento enviado');
      setEmailDialog(false);
    } catch (e) {
      toast.error(e.message || 'Error al enviar el email');
    }
    setSending(false);
  };

  const handleAddCat = async () => {
    if (!newCat.trim()) { toast.error('Escribe el nombre de la categoría'); return; }
    setCatSaving(true);
    try {
      const res = await legalAPI.addCategory(newCat.trim());
      setCategories(res.categories || []);
      toast.success('Categoría creada');
      setNewCat('');
      setCatDialog(false);
    } catch (e) {
      toast.error(e.message || 'Error al crear la categoría');
    }
    setCatSaving(false);
  };

  return (
    <div className="space-y-6" data-testid="legal-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Shield className="w-7 h-7 text-slate-700" /> Legal
          </h1>
          <p className="text-muted-foreground mt-1 max-w-2xl">
            Archivo documental de la empresa. Acceso exclusivo de las CEOs. Sube los documentos escaneados
            (no escribas datos sensibles como texto suelto: viven dentro del documento).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setCatDialog(true)} data-testid="add-category-btn">
            <FolderOpen className="w-4 h-4 mr-2" /> Nueva categoría
          </Button>
          <Button onClick={openUpload} data-testid="upload-doc-btn">
            <Upload className="w-4 h-4 mr-2" /> Subir documento
          </Button>
        </div>
      </div>

      {/* Search + category filter */}
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar documento por nombre, categoría o nota..."
            className="pl-9"
            data-testid="legal-search"
          />
        </div>
        <div className="w-full md:w-64">
          <Select value={activeCat} onValueChange={setActiveCat}>
            <SelectTrigger data-testid="legal-cat-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las categorías</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>{c}{countByCat[c] ? ` (${countByCat[c]})` : ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando...
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground">
            <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">{documents.length === 0 ? 'Aún no hay documentos. Sube el primero.' : 'No hay documentos que coincidan con la búsqueda.'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="legal-doc-list">
          {filtered.map((doc) => (
            <motion.div key={doc.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
              <Card data-testid={`legal-doc-${doc.id}`} className="hover:shadow-sm transition-shadow">
                <CardContent className="py-3 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center shrink-0">
                    <Paperclip className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{doc.nombre}</p>
                    <div className="flex items-center gap-2 flex-wrap mt-0.5">
                      <Badge variant="secondary" className="text-xs">{doc.categoria}</Badge>
                      <span className="text-xs text-muted-foreground">{fmtDate(doc.created_at)}</span>
                      {doc.size != null && <span className="text-xs text-muted-foreground">· {fmtSize(doc.size)}</span>}
                      {doc.nota && <span className="text-xs text-muted-foreground italic truncate">— {doc.nota}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" title="Descargar" onClick={() => handleDownload(doc)} data-testid={`download-doc-${doc.id}`}>
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" title="Enviar por email" onClick={() => openEmail(doc)} data-testid={`email-doc-${doc.id}`}>
                      <Mail className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" title="Editar" onClick={() => openEdit(doc)} data-testid={`edit-doc-${doc.id}`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" title="Eliminar" onClick={() => handleDelete(doc)} disabled={busyId === doc.id} data-testid={`delete-doc-${doc.id}`}>
                      {busyId === doc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4 text-red-500" />}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Upload / Edit dialog */}
      <Dialog open={docDialog} onOpenChange={setDocDialog}>
        <DialogContent className="max-w-lg" data-testid="legal-doc-dialog">
          <DialogHeader>
            <DialogTitle>{editingDoc ? 'Editar documento' : 'Subir documento'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {!editingDoc && (
              <div>
                <Label>Archivo (PDF, imagen, Word...)</Label>
                <Input
                  type="file"
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp,.heic"
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    setForm((p) => ({ ...p, file: f, nombre: p.nombre || (f ? f.name.replace(/\.[^.]+$/, '') : '') }));
                  }}
                  data-testid="legal-file-input"
                />
              </div>
            )}
            <div>
              <Label>Nombre del documento</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Ej: Escritura constitución 2021" data-testid="legal-nombre" />
            </div>
            <div>
              <Label>Categoría</Label>
              <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
                <SelectTrigger data-testid="legal-categoria"><SelectValue placeholder="Selecciona categoría" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nota (opcional)</Label>
              <Textarea rows={2} value={form.nota} onChange={(e) => setForm({ ...form, nota: e.target.value })} placeholder="Referencia breve del documento..." data-testid="legal-nota" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaveDoc} disabled={saving} data-testid="save-doc-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingDoc ? 'Guardar cambios' : 'Subir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New category dialog */}
      <Dialog open={catDialog} onOpenChange={setCatDialog}>
        <DialogContent className="max-w-sm" data-testid="legal-category-dialog">
          <DialogHeader><DialogTitle>Nueva categoría</DialogTitle></DialogHeader>
          <div className="py-2">
            <Label>Nombre de la categoría</Label>
            <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="Ej: Licencias / Permisos" data-testid="new-category-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatDialog(false)}>Cancelar</Button>
            <Button onClick={handleAddCat} disabled={catSaving} data-testid="save-category-btn">
              {catSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Crear
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email dialog */}
      <Dialog open={emailDialog} onOpenChange={setEmailDialog}>
        <DialogContent className="max-w-lg" data-testid="legal-email-dialog">
          <DialogHeader><DialogTitle>Enviar documento por email</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-2 text-sm p-2.5 rounded-lg bg-slate-50 border">
              <Paperclip className="w-4 h-4 text-slate-500" /> {emailDoc?.nombre}
            </div>
            <div>
              <Label>Email de destino</Label>
              <Input type="email" value={emailForm.to_email} onChange={(e) => setEmailForm({ ...emailForm, to_email: e.target.value })} placeholder="destinatario@email.com" data-testid="email-to" />
            </div>
            <div>
              <Label>Asunto</Label>
              <Input value={emailForm.subject} onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })} data-testid="email-subject" />
            </div>
            <div>
              <Label>Mensaje (opcional)</Label>
              <Textarea rows={3} value={emailForm.message} onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })} placeholder="Mensaje que acompaña al documento..." data-testid="email-message" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialog(false)}>Cancelar</Button>
            <Button onClick={handleSendEmail} disabled={sending} data-testid="send-email-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />} Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default LegalPage;
